import type { TripType } from '@ixigo/cab-core';
import { type ApiErrorResponse, executeApiCall } from '@/api';
import fetch from '@/lib/fetch';
import type { SearchFormData } from '../store';

export interface TripTypeResponse {
	data: {
		type: TripType;
		msg: string;
		minDurationInMinutes: number;
	};
}

type TripTypeSearchData = Required<Pick<SearchFormData, 'pickup' | 'drop'>>;

interface CacheEntry {
	data: TripTypeResponse | ApiErrorResponse<unknown>;
	timestamp: number;
	searchFormDataKey: string;
}

const tripTypeCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000;

function buildTripTypeRequest(searchFormData: TripTypeSearchData) {
	return {
		pickup: {
			latitude: searchFormData.pickup.lat,
			longitude: searchFormData.pickup.lng,
			placeId: searchFormData.pickup.placeId,
		},
		drop: {
			latitude: searchFormData.drop.lat,
			longitude: searchFormData.drop.lng,
			placeId: searchFormData.drop.placeId,
		},
	};
}

function generateCacheKey(searchFormData: TripTypeSearchData): string {
	return JSON.stringify(searchFormData);
}

function isCacheValid(entry: CacheEntry): boolean {
	return Date.now() - entry.timestamp < CACHE_TTL_MS;
}

function cleanExpiredCache(): void {
	const now = Date.now();
	for (const [key, entry] of tripTypeCache.entries()) {
		if (now - entry.timestamp >= CACHE_TTL_MS) {
			tripTypeCache.delete(key);
		}
	}
}

export const fetchTripType = async (
	searchFormData: TripTypeSearchData,
	abortController?: AbortSignal,
): Promise<TripTypeResponse | ApiErrorResponse<unknown>> => {
	const cacheKey = generateCacheKey(searchFormData);

	const cachedEntry = tripTypeCache.get(cacheKey);
	if (cachedEntry && isCacheValid(cachedEntry)) {
		return cachedEntry.data;
	}

	cleanExpiredCache();

	const request = buildTripTypeRequest(searchFormData);
	const response = await executeApiCall(
		() =>
			fetch
				.post<TripTypeResponse>('cabs/api/v1/trip-type', {
					body: JSON.stringify(request),
					headers: {
						'Content-Type': 'application/json',
					},
					signal: abortController,
					// Forward `?source=` from the page URL onto this request.
					includeSearchParams: ['source'],
				})
				.json(),
		'fetchTripType',
	);

	tripTypeCache.set(cacheKey, {
		data: response,
		timestamp: Date.now(),
		searchFormDataKey: cacheKey,
	});

	return response;
};
